// Состояние боковой панели справки, одно на приложение.
//
// Панель живёт выше маршрутов, поэтому при переходе между экранами не
// закрывается и помнит, что человек читал. Здесь же висит обработчик F1.

import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { resolveHelpTarget } from './helpContext';

// Панель грузится при первом открытии: вместе с ней приезжают тексты всех
// статей и таблицы Mantine, а это 120+ КБ, которые незачем держать в основном
// бандле у всех. Сам провайдер лёгкий — в нём только состояние и F1.
const HelpDrawer = lazy(() =>
  import('./HelpDrawer').then((module) => ({ default: module.HelpDrawer })),
);

interface HelpApi {
  opened: boolean;
  /** `<статья>#<раздел>` или null — оглавление. */
  target: string | null;
  /**
   * Открыть справку. Без аргумента — по текущему маршруту
   * (см. HELP_BY_ROUTE); для экрана без записи откроется оглавление.
   */
  openHelp: (target?: string) => void;
  /** Показать другую статью, не закрывая панель. */
  showArticle: (target: string) => void;
  closeHelp: () => void;
}

const HelpContext = createContext<HelpApi | null>(null);

/** Поля ввода: в них F1 не перехватываем. */
function isTextEntry(node: Element | null): boolean {
  if (!node) return false;
  const tag = node.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return (node as HTMLElement).isContentEditable === true;
}

export function HelpProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [opened, setOpened] = useState(false);
  // Открывали ли панель хоть раз: до первого открытия её не монтируем вовсе,
  // после — оставляем смонтированной, чтобы работала анимация закрытия.
  const [everOpened, setEverOpened] = useState(false);
  // Последняя открытая статья остаётся в состоянии: панель выше маршрутов,
  // поэтому переход на другой экран её не сбрасывает.
  const [target, setTarget] = useState<string | null>(null);

  const openHelp = useCallback(
    (explicit?: string) => {
      setTarget(resolveHelpTarget(explicit, pathname));
      setOpened(true);
      setEverOpened(true);
    },
    [pathname],
  );

  const showArticle = useCallback((next: string) => {
    setTarget(next);
    setOpened(true);
    setEverOpened(true);
  }, []);

  const closeHelp = useCallback(() => setOpened(false), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'F1' || event.ctrlKey || event.altKey || event.metaKey) return;
      // В поле ввода F1 оставляем браузеру: человек там печатает, а не ищет
      // справку по экрану.
      if (isTextEntry(document.activeElement)) return;
      event.preventDefault();
      openHelp();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openHelp]);

  const api = useMemo<HelpApi>(
    () => ({ opened, target, openHelp, showArticle, closeHelp }),
    [opened, target, openHelp, showArticle, closeHelp],
  );

  return (
    <HelpContext.Provider value={api}>
      {children}
      {everOpened && (
        <Suspense fallback={null}>
          <HelpDrawer
            opened={opened}
            target={target}
            onClose={closeHelp}
            onNavigate={showArticle}
          />
        </Suspense>
      )}
    </HelpContext.Provider>
  );
}

/**
 * Доступ к панели справки. Вне провайдера возвращает заглушку: кнопка «?» на
 * экране, отрисованном без провайдера (например в тесте страницы), не должна
 * ронять этот экран.
 */
export function useHelp(): HelpApi {
  return (
    useContext(HelpContext) ?? {
      opened: false,
      target: null,
      openHelp: () => {},
      showArticle: () => {},
      closeHelp: () => {},
    }
  );
}
